import { Injectable } from "@angular/core";
import * as CryptoJS from "crypto-js";
import { FILE_CONFIGURATION } from "../global/constants";
import { LocalStorageService } from "./storage-service/local-storage.service";

@Injectable({
  providedIn: "root",
})
export class CompanyContextService {
  private readonly organizationCompanyKey = "organizationCurrentCompany";

  constructor(private localStorageService: LocalStorageService) {}

  getGlobalCompanyId(): number | null {
    const currentCompany =
      this.localStorageService.getLocalStore("currentCompany");
    if (currentCompany && currentCompany !== "{}" && currentCompany !== "0") {
      const parsedCompanyId = Number(currentCompany);
      return Number.isFinite(parsedCompanyId) ? parsedCompanyId : null;
    }

    const authorization =
      this.localStorageService.getLocalStore("authorization");
    if (authorization && authorization !== "{}") {
      const decodedToken = this.localStorageService.decodeJWT(authorization);
      const tokenCompanyId = Number(decodedToken?.company_id);
      return Number.isFinite(tokenCompanyId) && tokenCompanyId > 0
        ? tokenCompanyId
        : null;
    }

    return null;
  }

  async getOrganizationCompanyId(): Promise<any> {
    return await this.localStorageService.getDataFromIndexedDB("companyId");
  }

  getOrganizationScopedCompanyId(): number | null {
    const organizationCompany = this.localStorageService.getLocalStore(
      this.organizationCompanyKey
    );
    if (
      organizationCompany &&
      organizationCompany !== "{}" &&
      organizationCompany !== "0"
    ) {
      const parsedCompanyId = Number(organizationCompany);
      return Number.isFinite(parsedCompanyId) ? parsedCompanyId : null;
    }

    return null;
  }

  getRequestCompanyId(pathname?: string): number | null {
    const currentPath =
      pathname ||
      (typeof window !== "undefined" ? window.location.pathname : "");

    if (currentPath.startsWith("/organization")) {
      return (
        this.getOrganizationScopedCompanyId() || this.getGlobalCompanyId()
      );
    }

    return this.getGlobalCompanyId();
  }

  async setOrganizationCompanyContext(companyId: any, fileSize?: any) {
    this.localStorageService.setLocalStore(
      this.organizationCompanyKey,
      String(companyId)
    );
    const encryptedCompanyId = CryptoJS.AES.encrypt(
      String(companyId).trim(),
      ""
    ).toString();
    await this.localStorageService.setDataInIndexedDB(
      "companyId",
      encryptedCompanyId
    );
    await this.localStorageService.setDataInIndexedDB(
      "companyIdFileSize",
      fileSize ?? FILE_CONFIGURATION.DEFAULT_FILE_SIZE
    );
    return encryptedCompanyId;
  }

  async setGlobalCompanyContext(companyDetails: any, jwtToken?: string) {
    if (!companyDetails?.id) {
      return null;
    }

    if (jwtToken) {
      this.localStorageService.setLocalStore("authorization", jwtToken);
    }

    const companyId = Number(companyDetails.id);
    this.localStorageService.setLocalStore("currentCompany", String(companyId));

    const encryptedCompanyId = CryptoJS.AES.encrypt(
      String(companyId).trim(),
      ""
    ).toString();

    await this.localStorageService.setDataInIndexedDB(
      "selectedCompanyId",
      encryptedCompanyId
    );
    await this.localStorageService.setDataInIndexedDB(
      "selectedCompanyName",
      companyDetails?.company_name || null
    );
    await this.localStorageService.setDataInIndexedDB(
      "selectedCompanyData",
      companyDetails || null
    );
    await this.localStorageService.setDataInIndexedDB(
      "selectedCompanyFileSize",
      companyDetails?.file_size || FILE_CONFIGURATION.DEFAULT_FILE_SIZE
    );

    return encryptedCompanyId;
  }
}
